import React, { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from 'chart.js';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Box, Container } from '@mui/material';
import ParentNavbar from '../student/ParentNavbar';
import '../student/MockTestList.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface Quiz {
    _id: string;
    totalQuestions: number;
    correctAnswers: number;
    incorrectAnswers: number;
    skippedQuestions: number;
    percentage: number;
    topics: string[];
    date: string;
}

interface ApiResponse {
    quizzes: Quiz[];
    currentPage: number;
    totalPages: number;
}

const fetchChildSubjectHistory = async (
    childId: string,
    subject: string,
    page: number
): Promise<ApiResponse> => {
    const token = localStorage.getItem('token');
    const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/parent/children/${childId}/subject-history/${subject}?page=${page}`,
        { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
};

const TopicList: React.FC<{ topics: string[] }> = ({ topics }) => {
    const [showAll, setShowAll] = useState(false);
    const VISIBLE_LIMIT = 2;
    const remainingCount = topics.length - VISIBLE_LIMIT;
    const visibleTopics = showAll ? topics : topics.slice(0, VISIBLE_LIMIT);

    return (
        <div className="topics-list">
            {visibleTopics.map((topic, index) => (
                <span key={index} className="topic-tag">
                    {topic}
                </span>
            ))}
            {!showAll && remainingCount > 0 && (
                <span
                    className="topic-tag topic-tag--more"
                    onClick={(e) => { e.stopPropagation(); setShowAll(true); }}
                >
                    +{remainingCount} more
                </span>
            )}
        </div>
    );
};

const ParentMockTestList: React.FC = () => {
    const { childId, subject } = useParams<{ childId: string; subject: string }>();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);

    const getCssVar = useCallback((name: string, fallback: string) => {
        if (typeof window === 'undefined') return fallback;
        const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
        return value || fallback;
    }, []);

    const { data, isLoading, isError } = useQuery<ApiResponse>({
        queryKey: ['parentChildSubjectHistory', childId, subject, currentPage],
        queryFn: () => fetchChildSubjectHistory(childId!, subject!, currentPage),
        enabled: !!childId && !!subject,
    });

    if (isLoading) return <div className="loading">Loading...</div>;
    if (isError) return <div className="error">Error fetching data</div>;
    if (!data) return null;

    const sortedQuizzes = [...data.quizzes].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    const chartData = {
        labels: sortedQuizzes.map((quiz) => new Date(quiz.date).toLocaleDateString()),
        datasets: [
            {
                label: 'Test Scores',
                data: sortedQuizzes.map((quiz) => quiz.percentage),
                borderColor: 'rgba(59, 130, 246, 1)',
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                fill: true,
                tension: 0.4,
                pointRadius: 5,
                pointHoverRadius: 8,
                pointBackgroundColor: 'rgba(59, 130, 246, 1)',
                pointBorderColor: getCssVar('--surface-0', '#fff'),
                pointBorderWidth: 2,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top' as const,
                labels: { font: { size: 14 }, color: getCssVar('--text-2', '#1e293b') },
            },
            title: {
                display: true,
                text: `${subject} Test Performance`,
                font: { size: 20 },
                color: getCssVar('--text-1', '#1e293b'),
            },
            tooltip: {
                backgroundColor: getCssVar('--surface-2', '#1e293b'),
                titleColor: getCssVar('--text-1', '#fff'),
                bodyColor: getCssVar('--text-1', '#fff'),
                titleFont: { size: 14 },
                bodyFont: { size: 12 },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: 'Score (%)',
                    font: { size: 14 },
                    color: getCssVar('--text-2', '#1e293b'),
                },
                ticks: { color: getCssVar('--text-3', '#64748b') },
                grid: { color: getCssVar('--border-2', 'rgba(0, 0, 0, 0.1)') },
            },
            x: {
                title: {
                    display: true,
                    text: 'Test Date',
                    font: { size: 14 },
                    color: getCssVar('--text-2', '#1e293b'),
                },
                ticks: { color: getCssVar('--text-3', '#64748b') },
                grid: { display: false },
            },
        },
    };

    return (
        <Box sx={(theme) => ({ minHeight: '100vh', backgroundColor: theme.palette.background.default })}>
            <ParentNavbar />
            <Container maxWidth="xl" sx={{ pt: 12, pb: 4 }}>
                <div className="test-list-container">
                    <button
                        className="pagination-button"
                        onClick={() => navigate(`/parent/student/${childId}/scores`)}
                        style={{ marginBottom: '16px' }}
                    >
                        ← Back to Scoreboard
                    </button>
                    <h1 className="test-list-title">{subject} Mock Test Results</h1>
                    <div className="chart-container">
                        <Line data={chartData} options={chartOptions} />
                    </div>
                    <h2 className="test-list-subtitle">Test History</h2>
                    <div className="test-grid">
                        {data.quizzes.length > 0 ? (
                            <>
                                <div className="test-cards">
                                    {data.quizzes.map((quiz) => (
                                        <div key={quiz._id} className="test-history-card">
                                            <div className="card-header">
                                                <div className="test-date">
                                                    {new Date(quiz.date).toLocaleDateString()}
                                                </div>
                                                <div className="test-score">{quiz.percentage.toFixed(1)}%</div>
                                            </div>
                                            <div className="card-stats">
                                                <div className="stat-item">
                                                    <span className="stat-label">Total Questions:</span>
                                                    <span className="stat-value">{quiz.totalQuestions}</span>
                                                </div>
                                                <div className="stat-item correct">
                                                    <span className="stat-label">Correct:</span>
                                                    <span className="stat-value">{quiz.correctAnswers}</span>
                                                </div>
                                                <div className="stat-item incorrect">
                                                    <span className="stat-label">Incorrect:</span>
                                                    <span className="stat-value">{quiz.incorrectAnswers}</span>
                                                </div>
                                                <div className="stat-item skipped">
                                                    <span className="stat-label">Skipped:</span>
                                                    <span className="stat-value">{quiz.skippedQuestions}</span>
                                                </div>
                                            </div>
                                            <div className="card-topics">
                                                <div className="topics-label">Topics:</div>
                                                <TopicList topics={quiz.topics} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {data.totalPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            className="pagination-button"
                                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                        <span className="page-info">
                                            Page {currentPage} of {data.totalPages}
                                        </span>
                                        <button
                                            className="pagination-button"
                                            onClick={() =>
                                                setCurrentPage((prev) => Math.min(prev + 1, data.totalPages))
                                            }
                                            disabled={currentPage === data.totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p>No tests found for {subject}.</p>
                        )}
                    </div>
                </div>
            </Container>
        </Box>
    );
};

export default ParentMockTestList;
